/** A record, read-only.
 *
 * Double-clicking a row opens THIS, not the edit form. Looking at something
 * and changing it are different intentions, and a list is scanned far more
 * often than it is edited — so the cheap gesture should be the safe one.
 *
 * Editing stays one deliberate click away, here or from the row's own Edit
 * link. A record opened by accident closes with nothing changed.
 *
 * Masked columns stay masked: a caller without `view_values` never receives
 * those fields from the server, so there is nothing here to hide — the field
 * simply says so. UI_TASTE.md, the Mask right.
 */

import type { FormField, MasterSpec } from '@/types'
import { singularOf } from '@/lib/label'

const YES_NO: Record<string, string> = { true: 'Yes', false: 'No' }

/** Values are shown as people read them, not as the database holds them. */
function display(field: FormField, raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  const v = String(raw)
  if (typeof raw === 'boolean') return YES_NO[v]
  if (field.type === 'select') {
    return field.options?.find(o => o.value === v)?.label ?? v
  }
  if (field.type === 'date') {
    // DD-MM-YYYY, app-wide. UI_TASTE.md.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
    return m ? `${m[3]}-${m[2]}-${m[1]}` : v
  }
  return v
}

export function MasterDetail(
  { spec, record, valuesVisible, canEdit, onEdit, onClose }: {
    spec: MasterSpec
    record: Record<string, unknown>
    valuesVisible: boolean
    /** Holds `modify`. Without it the record is read, not offered for
     *  change. RIGHTS_MODEL.md 6.1. */
    canEdit: boolean
    onEdit: () => void
    onClose: () => void
  },
) {
  const fields = (spec.fields ?? []).filter(f => f.key !== spec.idKey)
  const code = String(record[spec.idKey] ?? '')
  const title = String(record.name ?? record.item_name ?? code)

  return (
    <div className="card detail">
      <div className="card-head detail-head">
        <div>
          <div className="detail-kind">{singularOf(spec)}</div>
          <h2 className="detail-title">{title}</h2>
          {/* The code is shown only where it means something. Where the
              system issues it, nobody refers to a supplier by number. */}
          {!spec.idGenerated && code && (
            <div className="detail-code">{code}</div>
          )}
        </div>
        <div className="detail-actions">
          {spec.fields && canEdit && (
            <button className="btn btn-secondary" onClick={onEdit}>Edit</button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <dl className="detail-grid">
        {fields.map(f => {
          const masked = f.value && !valuesVisible
          return (
            <div key={f.key}
                 className={f.type === 'textarea' ? 'detail-item span-2'
                                                  : 'detail-item'}>
              <dt>{f.label}</dt>
              <dd className={masked ? 'masked' : undefined}>
                {masked ? '••••' : display(f, record[f.key])}
              </dd>
            </div>
          )
        })}

        {spec.hasStatus !== false && (
          <div className="detail-item">
            <dt>Status</dt>
            <dd>
              <span className={`badge badge-${String(record.status ?? '').toLowerCase()}`}>
                {String(record.status ?? '—')}
              </span>
            </dd>
          </div>
        )}

        {record.updated_at != null && (
          <div className="detail-item">
            <dt>Last Changed</dt>
            <dd>
              {String(record.updated_at).slice(0, 16).replace('T', ' ')}
              {record.updated_by ? ` by ${String(record.updated_by)}` : ''}
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}
